import React, { useCallback, useEffect, useRef, useState } from "react";
import { listJobs, deleteJob } from "./bulkClassifyApi";
import {
  jobRowView,
  presetRange,
  buildListQuery,
  myUserIdFromToken,
  canDelete,
  statusMatches,
  serverStatusParam,
  statusLabel,
} from "./jobListView";

const inputStyle = {
  padding: "4px 8px",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  fontSize: 13,
};
const miniBtn = {
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#374151",
  borderRadius: 6,
  padding: "3px 10px",
  fontSize: 12,
  cursor: "pointer",
};

/** 잡 이력(그룹 공유) — 기간(KST)·구분·상태 필터 + 본인 잡 삭제. RUNNING/PENDING 존재 시 5초 재조회. */
export default function BulkJobList({ onOpen, refreshKey }) {
  const [jobs, setJobs] = useState(null); // null=로딩, []=비어있음
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState(() => ({
    ...presetRange("week"),
    source: "",
    status: "",
  }));
  const timerRef = useRef(null);
  const queryRef = useRef(
    buildListQuery({ ...presetRange("week"), limit: 50 }),
  );
  const appliedStatusRef = useRef(""); // 검색 시점에 적용된 상태 필터(드롭다운 변경≠즉시 적용)
  const reqSeqRef = useRef(0); // 응답 순서 가드 — 뒤늦은(스테일) 응답이 최신 결과를 덮지 않게
  const myId = myUserIdFromToken(
    localStorage.getItem("token") || sessionStorage.getItem("token") || "",
  );

  const load = useCallback(async () => {
    const seq = ++reqSeqRef.current;
    try {
      const d = await listJobs(queryRef.current);
      if (seq !== reqSeqRef.current) return; // 더 새 요청이 나감 — 이 응답 폐기
      setJobs(Array.isArray(d.jobs) ? d.jobs : []);
      setLoadError(false);
    } catch (e) {
      if (seq !== reqSeqRef.current) return;
      setJobs([]);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    const live = (jobs || []).some(
      (j) => j.status === "RUNNING" || j.status === "PENDING",
    );
    if (live && timerRef.current == null) {
      timerRef.current = setInterval(load, 5000);
    }
    if (!live && timerRef.current != null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current != null) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [jobs, load]);

  const onSearch = useCallback(() => {
    queryRef.current = buildListQuery({
      from: filters.from,
      to: filters.to,
      source: filters.source,
      status: serverStatusParam(filters.status),
      limit: 200,
    });
    appliedStatusRef.current = filters.status;
    setJobs(null); // 로딩 표시
    load();
  }, [filters, load]);

  const applyPreset = useCallback((kind) => {
    setFilters((f) => ({ ...f, ...presetRange(kind) }));
  }, []);

  const onDelete = useCallback(
    async (e, jobId) => {
      e.stopPropagation(); // 행 클릭(열람)과 분리
      if (!window.confirm("이 이력을 삭제할까요?")) return;
      try {
        await deleteJob(jobId);
        load();
      } catch (err) {
        alert(err.message);
      }
    },
    [load],
  );

  const visible = (jobs || []).filter((j) =>
    statusMatches(j, appliedStatusRef.current),
  );

  const th = {
    border: "1px solid #e5e7eb",
    padding: "4px 10px",
    background: "#f9fafb",
    fontWeight: 600,
    textAlign: "left",
  };
  const td = { border: "1px solid #e5e7eb", padding: "4px 10px" };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
        분류 이력 (그룹 공유)
      </div>
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 10,
          fontSize: 13,
        }}
      >
        등록일
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          style={inputStyle}
        />
        ~
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          style={inputStyle}
        />
        <button style={miniBtn} onClick={() => applyPreset("today")}>
          오늘
        </button>
        <button style={miniBtn} onClick={() => applyPreset("week")}>
          최근 7일
        </button>
        <button style={miniBtn} onClick={() => applyPreset("month")}>
          이번달
        </button>
        <select
          value={filters.source}
          onChange={(e) =>
            setFilters((f) => ({ ...f, source: e.target.value }))
          }
          style={inputStyle}
        >
          <option value="">구분 전체</option>
          <option value="api">API</option>
          <option value="file">파일</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) =>
            setFilters((f) => ({ ...f, status: e.target.value }))
          }
          style={inputStyle}
        >
          <option value="">상태 전체</option>
          <option value="DONE">완료</option>
          <option value="LIVE">진행중</option>
          <option value="RECEIVED">분류 대기</option>
          <option value="FAILEDX">실패</option>
        </select>
        <button
          style={{
            ...miniBtn,
            background: "#2563eb",
            color: "#fff",
            border: "none",
          }}
          onClick={onSearch}
        >
          검색
        </button>
      </div>

      {jobs == null && (
        <div style={{ color: "#6b7280" }}>목록 불러오는 중…</div>
      )}
      {jobs != null && loadError && (
        <div style={{ color: "#6b7280" }}>
          목록을 불러오지 못했습니다.{" "}
          <button onClick={load} style={miniBtn}>
            재시도
          </button>
        </div>
      )}
      {jobs != null && !loadError && !visible.length && (
        <div style={{ color: "#6b7280" }}>
          조건에 맞는 분류 이력이 없습니다. 파일을 업로드하거나 DocuAI에서
          "loginHS로 보내기"를 사용하세요.
        </div>
      )}
      {jobs != null && !loadError && visible.length > 0 && (
        <table style={{ borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={th}>구분</th>
              <th style={th}>일시</th>
              <th style={th}>보낸 ID</th>
              <th style={th}>이름</th>
              <th style={th}>건수</th>
              <th style={th}>상태</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((j) => {
              const v = jobRowView(j);
              return (
                <tr
                  key={v.jobId}
                  onClick={() => onOpen && onOpen(v.jobId)}
                  style={{ cursor: "pointer" }}
                >
                  <td style={td}>
                    <span
                      style={{
                        padding: "1px 8px",
                        borderRadius: 10,
                        fontSize: 12,
                        background: v.isApi ? "#dbeafe" : "#f3f4f6",
                        color: v.isApi ? "#1d4ed8" : "#374151",
                      }}
                    >
                      {v.badge}
                    </span>
                  </td>
                  <td style={td}>{v.when}</td>
                  <td style={td}>{v.sender}</td>
                  <td style={td}>{v.label}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {v.count}
                    {v.orig > v.count ? ` (${v.orig})` : ""}
                  </td>
                  <td style={td}>
                    {statusLabel(v.status)}
                    {v.progress ? ` (${v.progress})` : ""}
                  </td>
                  <td style={td}>
                    {canDelete(j, myId) && (
                      <span
                        onClick={(e) => onDelete(e, v.jobId)}
                        style={{
                          color: "#9ca3af",
                          fontSize: 12,
                          cursor: "pointer",
                        }}
                      >
                        삭제
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
